import React from 'react'
import { createRoot } from 'react-dom/client'
import axios from 'axios'
import { batch } from 'react-redux'
import { Spinner } from '@8x8/oxygen-loaders'

import { getEnvironmentData } from '~/core/env'
import axiosService, { setAxios } from '~/services/axios'
import store from '~/core/store'
import { initTheme, setViewedSession } from '~/actions/ui'
import { setInitData } from '~/actions/app'
import Settings from './settings'
import Unauthorized from './unauthorized'
import { initTranslate } from '~/core/translate/translate'
import { getLocale, getJitsiUserIdentity } from '~/core/utils'
import { APP_TYPES } from '~/core/constants'

import { initAnalytics } from '~/services/analytics'
import integrations from '~/services/integrations'
import { getMeetingProperties, setUserIdentity } from '~/actions/room-settings'
import { SpinnerContainer } from '~/components/room-configuration/components'

const container = document.getElementById('container')

createRoot(container).render(<SpinnerContainer><Spinner /></SpinnerContainer>)

const initSettingsApp = async () => {
  const { location: { hash, search }, origin } = window
  const searchParams = new URLSearchParams(search)
  const hashParams = new URLSearchParams(hash.slice(1))
  const token = hashParams.get('token') || searchParams.get('token')
  const meetingUrl = hashParams.get('room') || searchParams.get('room')
  const meetingFqn = meetingUrl?.split('/')?.slice(-2)?.join('/')

  const env = searchParams.get('env')
  const theme = searchParams.get('theme') || 'light'

  await initTranslate(getLocale(), origin)
  await store.dispatch(initTheme(theme))

  if (!meetingUrl || !token) {
    createRoot(container).render(<Unauthorized />)
    return
  }

  const userIdentity = getJitsiUserIdentity(token)
  store.dispatch(setUserIdentity(userIdentity))

  const isPilot = env === 'pilot' || origin.includes('pilot')
  const hostname = isPilot ? 'https://vo-8x8-meet-pilot.jitsi.net' : 'https://vo-8x8-meet.jitsi.net'
  const conferenceId = isPilot ? `https://stage.8x8.vc/${meetingFqn}` : `https://8x8.vc/${meetingFqn}`

  integrations.init()

  await getEnvironmentData({ hostname })
  userIdentity && await initAnalytics(userIdentity.id)

  setAxios(axios.create())
  axiosService.setup({ token, checkForExpiry: false })

  try {
    await store.dispatch(getMeetingProperties(meetingFqn))
  } catch (e) {
    createRoot(container).render(<Unauthorized />)

    return
  }

  batch(() => {
    store.dispatch(setViewedSession(meetingFqn))
    store.dispatch(setInitData({
      appType: APP_TYPES.SETTINGS,
      authenticated: true,
      initialized: true
    }))
  })

  createRoot(container).render(<Settings room={conferenceId} />)
}

initSettingsApp()
